import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FeatureCard } from "@/components/ui/grid-feature-cards";
import { InfiniteTestimonialSlider } from "@/components/ui/infinite-testimonial-slider";
import { Rocket, TrendingUp, Search, ArrowRight, FileText, Presentation, BarChart3, Settings, Zap, Share } from "lucide-react";
import { motion } from "motion/react";
import PricingPreview from "@/components/PricingPreview";
import CTASection from "@/components/CTASection";
import ClientLogosSection from "@/components/ClientLogosSection";
import FinalCTA from "@/components/FinalCTA";
import SeoFaqSection from "@/components/SeoFaqSection";

const billingFaqs = [
  { question: "How does billing work?", answer: "Choose your plan, enter your payment details, and receive invoices monthly or annually—simple, transparent, and hassle-free. You'll be charged when you sign up and again on each renewal date. Your next billing date is always shown on your Subscription Page." },
  { question: "What payment methods do you accept?", answer: "We accept all major credit and debit cards (Visa, Mastercard, American Express, JCB), and Link (by Stripe). PayPal is not supported at this time." },
  { question: "Can I pay by invoice?", answer: "Yes you can. However, payment by invoice is available only for Enterprise plan users. <a href='/enterprise#meet-the-crew' class='text-primary hover:underline'>Reach out to our sales team</a> to set this up." },
  { question: "What currencies does gimmefy accept?", answer: "All plans are priced in US dollars (USD). For your reference, our payment processor shows local currency equivalents at checkout. Enterprise clients in select regions can request invoices in additional major currencies." },
  { question: "Can I add more than 5 team members to my organization?", answer: "Yes, but you'll need to upgrade to an Enterprise plan! For bigger teams, simply <a href='/enterprise#meet-the-crew' class='text-primary hover:underline'>contact the sales team</a>, and we'll tailor an Enterprise plan just for you." },
  { question: "Can I change my plan mid-cycle?", answer: "Yes! Upgrade, downgrade, or switch plans anytime by clicking Edit Subscription on your Subscription Page. Changes take effect on your next billing date, which is always visible on your account." },
  { question: "How can I purchase add-on credits? Do credits expire?", answer: "Click Buy Credits (in \"View Detailed Usage History\" on your Subscription Page) to top up anytime. Purchased credits never expire—use them whenever you need." },
  { question: "Can I pause my subscription?", answer: "Yes! Please <a href='/contact' class='text-primary hover:underline'>contact our support team</a> and we'll help with a flexible pause option." },
  { question: "What if I need to cancel?", answer: "You can cancel anytime and instantly, right from your account—no need to call or email. Just go to your Subscription Page, click Edit Subscription (in \"View Detailed Usage History\"), and follow the steps. Your cancellation is immediate and fully self-managed." },
  { question: "Still have a question about billing, payments, or your plan?", answer: "Drop us a message via our <a href='/contact' class='text-primary hover:underline'>contact form</a>. We're always happy to help!" }
];

const StartupPage = () => {
  return <div className="min-h-screen">
    <Navigation />

    {/* Hero Section */}
    <section className="py-32 bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center space-y-12">
          {/* Text Content - Center */}
          <div className="space-y-8">
            <motion.div className="space-y-8" initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8
            }}>
              <div className="space-y-6">
                <h1 id="startup-hero-heading" className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
                All Your Marketing, One Platform.
                  <span className="bg-gradient-primary bg-clip-text text-transparent"> Expert-Driven, Budget-Friendly, AI-Powered</span>
                </h1>
                <p id="startup-hero-description" className="text-base text-muted-foreground sm:text-lg lg:text-xl">From late-night ideas to investor decks and landing pages, tackle all your marketing content in one place. Powered by marketing expertise and best-in-class AI—so you save money, stay on-brand, and skip the tool chaos.</p>
              </div>

              <motion.div className="flex flex-col items-center gap-4 pt-2" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4,
                duration: 0.6
              }}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button id="startup-cta-launch-now" variant="hero" size="lg" className="group text-lg sm:text-xl px-8 py-6 sm:px-12 sm:py-8 min-h-[48px] w-full sm:w-auto" onClick={() => window.location.href = '/signup/free'}>
                    Launch Now
                    <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
                <p id="startup-cta-note" className="text-sm text-muted-foreground">No credit card. Get free credits.</p>
              </motion.div>
            </motion.div>
          </div>

          {/* Video - Below Text */}
          <div className="w-full max-w-4xl mx-auto">
            <div className="relative">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                  src="https://www.youtube.com/embed/-6cZsYD4-jY?si=UOTMZRkQFeDfrvmG&autoplay=1&mute=1&rel=0&loop=1&playlist=-6cZsYD4-jY"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ClientLogosSection />

      {/* Startup Accelerator Tools Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - Title, Subtitle & Information */}
              <div className="space-y-12">
                {/* Title and Subtitle */}
                <motion.div
                  className="text-left"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 id="startup-accelerator-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">Startup Accelerator Tools</h2>
                  <p id="startup-accelerator-description" className="text-lg sm:text-xl text-muted-foreground max-w-3xl">
                    Everything you need to launch faster and scale smarter
                  </p>
                </motion.div>

                {/* Feature Cards */}
                <div className="space-y-8">
                  {[
                    {
                      icon: FileText,
                      title: "Startup Launch Playbooks",
                      description: "Pre-built GTM flows from research to release"
                    },
                    {
                      icon: Presentation,
                      title: "Investor-Ready Pitch Decks",
                      description: "Slide outlines, talking points, speaker notes generated in one click"
                    },
                    {
                      icon: Search,
                      title: "Auto-Research Insights",
                      description: "Market sizing & competitor scans embedded in every brief"
                    }
                  ].map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.8 + (index * 0.1), duration: 0.6 }}
                      >
                        <Card className="p-4 sm:p-6 bg-gradient-card border-border shadow-elegant hover:shadow-elegant-hover transition-all duration-300 group hover:scale-105 active:scale-95">
                          <div className="flex items-start space-x-3 sm:space-x-4">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-primary rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                              <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                            </div>
                            <div className="space-y-1 sm:space-y-2">
                              <h3 className="text-lg sm:text-xl font-bold text-foreground">{feature.title}</h3>
                              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{feature.description}</p>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Right Column - Image */}
              <motion.div
                className="relative lg:order-last order-first"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                <div className="relative">
                  <img
                    src="/lovable-uploads/07675b27-ad82-4bf1-8f37-bb9c98e58765.jpg"
                    alt="Startup founder working at laptop with conference badge"
                    className="w-full max-w-sm mx-auto lg:max-w-none h-auto rounded-2xl shadow-elegant"
                    width={456}
                    height={591}
                  />
                  <div className="absolute inset-0 bg-gradient-primary/10 rounded-2xl"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

    {/* Pain → Promise - Scroll 2 */}
    <section className="py-24 bg-gradient-accent">
      <div className="container mx-auto px-6 text-center">
        <div className="max-w-5xl mx-auto space-y-8">
          <motion.p className="text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }}>
            Bootstrapped teams bleed time juggling research docs, freelancers, and five different tools.
          </motion.p>
          <motion.p className="text-lg sm:text-xl md:text-2xl text-foreground font-semibold leading-relaxed" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }}>
            gimmefy pulls every stage—insight, copy, visuals, scheduling—into one chat so you can launch in days, not months.
          </motion.p>
          <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} viewport={{
            once: true
          }} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 border border-primary/20">
            <span className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">85%</span>
            <span className="text-foreground font-medium">faster go-to-market time</span>
          </motion.div>
        </div>
      </div>
    </section>

    <CTASection title="Know a founder who wants to accelerate growth?" subtitle="Send them an invite to gimmefy for Startups—bonus signup credits included to jumpstart their marketing journey." buttonText="Tell a friend" buttonLink="/tellafriend" />

    {/* Why Startups Choose gimmefy */}
    <section className="py-32 bg-background">
      <div className="container mx-auto px-6">
        <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Why Startups Choose gimmefy
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {[{
            icon: Rocket,
            title: "Launch Faster",
            description: "Go from idea to market in days, not months with step-by-step Playbooks that include insights, copy, visuals, and scheduling in one chat."
          }, {
            icon: TrendingUp,
            title: "Investor-Ready",
            description: "Hit milestones without extra hires. Studio, trained by marketers and driven by leading AI, creates decks, ads, and campaigns with ease."
          }, {
            icon: BarChart3,
            title: "Content That Works",
            description: "Better engagement, faster traction with framework-driven assets built for audience, channel, and timing."
          }].map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }}>
              <Card className="p-6 sm:p-8 h-full bg-gradient-card border-border shadow-elegant hover:shadow-elegant-hover transition-all duration-300 group hover:scale-105 active:scale-95">
                <div className="text-center space-y-4 sm:space-y-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-primary rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </Card>
            </motion.div>;
          })}
        </div>
      </div>
    </section>

    {/* How It Works - Scroll 3 */}
    <section className="py-24">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Headline */}
          <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="space-y-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold bg-gradient-primary bg-clip-text text-transparent leading-tight">
              How It Works
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-lg">
              Four simple steps to transform your startup's marketing from chaos to clarity
            </p>
          </motion.div>

          {/* Right Column - 2x2 Grid */}
          <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {[{
              icon: Settings,
              title: "Brand Vault in 5 min",
              description: "Drop your logo, tone, and value prop.",
              color: "bg-gradient-to-br from-primary to-primary-glow"
            }, {
              icon: Zap,
              title: "One Prompt, Many Assets",
              description: "Studio spins landing headlines, ad copy, social teasers, and email intros in one pass.",
              color: "bg-gradient-to-br from-green-500 to-green-600"
            }, {
              icon: Rocket,
              title: "Run the Launch Playbook",
              description: "Automated stack produces market analysis, content calendar, and visual ads—ready to tweak.",
              color: "bg-gradient-to-br from-blue-500 to-blue-600"
            }, {
              icon: Share,
              title: "Edit & Publish",
              description: "Fine-tune in the Result Editor, then push live to LinkedIn, Instagram, or Facebook.",
              color: "bg-gradient-to-br from-purple-500 to-purple-600"
            }].map((feature, index) => <FeatureCard key={index} feature={feature} index={index} className="h-full" />)}
          </motion.div>
        </div>
      </div>
    </section>

    {/* Founder Quotes - Scroll 4 */}
    <section className="py-24 bg-gradient-accent">
      <div className="container mx-auto px-6">
        <InfiniteTestimonialSlider
          testimonials={[
            { quote: "Three weekends, one stack—closed our pre-seed in 14 days.", author: "Maya Patel", company: "Founder", avatar: "/src/assets/maya-patel-testimonial.jpg" },
            { quote: "We hit 1,000 sign-ups before hiring marketing #1. gimmefy was our whole content team.", author: "Leo Martinez", company: "CEO", avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/17993416-5bed-4614-94da-b6c4daa89ed8/keta_07.jpg?content-type=image%2Fjpeg" },
            { quote: "Investor updates write themselves, so I spend Fridays building, not formatting.", author: "Zara Elmina", company: "Co-founder", avatar: "/src/assets/zara-elmina-testimonial.jpg" },
            { quote: "From idea to paying customers without a marketing team.", author: "El Sarah", company: "Founder", avatar: "/src/assets/el-sarah-testimonial.jpg" },
            { quote: "Runway lasted 40% longer by eliminating agency costs.", author: "Marcus", company: "Co-founder", avatar: "/src/assets/marcus-testimonial.jpg" }
          ]}
        />
      </div>
    </section>

    {/* CTA Heading - Scroll 6 */}
    <section className="py-16 bg-gradient-hero-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle opacity-30"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(260,75%,60%,0.1),transparent_50%)]"></div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }}>
          <h2 id="startup-cta-heading" className="text-5xl md:text-7xl font-bold text-foreground leading-tight">
            Turn roadmap into
            <span className="bg-gradient-primary bg-clip-text text-transparent"> runway</span>
          </h2>
        </motion.div>
      </div>
    </section>

    {/* Pricing Section */}
    <div id="startup-pricing-section">
      <PricingPreview planTypes={['Free', 'Solo', 'Team']} idPrefix="startup-pricing" />
    </div>
    
    <SeoFaqSection faqs={billingFaqs} />
    
    <FinalCTA title="Build marketing magic— <br/> without breaking your stride." subtitle="Ready to fuel your startup's growth?" />
    <FooterWithNewsletter />
  </div>;
};
export default StartupPage;